import React from 'react';
import { Package } from 'lucide-react';

/**
 * The frame of the public tracking pages.
 *
 * These are the only screens in the app seen by somebody who is not our user,
 * so they sit outside `AppShell`: no sidebar, no activity centre, no socket —
 * all of which read the signed-in account. What they do get for free is the
 * theme, because the `dark` class lives on `<html>`, above the router, and
 * follows the visitor's own system preference on a first visit.
 *
 * `brand` is what replaces our wordmark when the order is managed by a partner.
 * The person waiting for the parcel bought from that partner and has never
 * heard of us, so the header says their name instead.
 *
 * @param {object} props
 * @param {string} [props.brand] - Name to print instead of the Ninja Hubs mark.
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {React.ReactNode} props.children
 * @param {string} [props.testId]
 */
export const TrackingLayout = ({ brand, title, subtitle, children, testId }) => (
  <div
    data-testid={testId}
    className="min-h-screen bg-ninja-gray dark:bg-[#0f0f0f] px-4 py-10 sm:py-14"
  >
    {/* Decorative, and behind everything: the page has to read without it. */}
    <div className="fixed inset-0 -z-10 pointer-events-none overflow-hidden">
      <div className="absolute -top-32 -right-24 w-80 h-80 bg-ninja-greenLight rounded-full blur-[120px]" />
      <div className="absolute -bottom-40 -left-24 w-80 h-80 bg-ninja-greenLight rounded-full blur-[120px]" />
    </div>

    <div className="w-full max-w-3xl mx-auto">
      <header className="flex flex-col items-center text-center mb-8">
        <div className="w-14 h-14 bg-ninja-greenLight backdrop-blur-md rounded-2xl flex items-center justify-center mb-4 border border-ninja-green/20 shadow-lg">
          <Package size={28} className="text-ninja-green" />
        </div>

        {brand ? (
          <p
            data-testid="tracking-brand"
            className="text-sm text-text-secondary font-medium mb-2"
          >
            Pedido gestionado por{' '}
            <span className="font-bold text-ninja-dark dark:text-white">{brand}</span>
          </p>
        ) : (
          <p className="text-xl font-extrabold text-ninja-green tracking-tight mb-2">
            Ninja <span className="text-ninja-dark dark:text-white">Hubs</span>
          </p>
        )}

        <h1 className="text-2xl sm:text-3xl font-extrabold text-ninja-dark dark:text-white">
          {title}
        </h1>
        {subtitle && (
          <p className="text-text-secondary mt-2 max-w-lg">{subtitle}</p>
        )}
      </header>

      {children}
    </div>
  </div>
);
